import {
  Component,
  DestroyRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { PdqCollapsed } from '@storybook/components/collapsed/collapsed.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { CheckoutSettings, CurrencySign } from '@pdqdev/pdq-shared/dist/entities';
import {
  ShippingMethodDiscountRule,
  ShippingMethodDiscountRange,
} from '@pdqdev/pdq-shared/dist/entities/CheckoutSettings';
import { Shippings } from '@pages/checkout-app/types/model';
import { ShippingMethod } from '@pages/checkout-app/types/types';
import { DropdownOption } from '@storybook/components/dropdown/dropdown.component';
import { ShopService } from '@services/shops/shop.service';
import { rateHasAnyDiscount } from '@pages/checkout-app/utils/string.utils';

const LIVE_RATE_CATEGORIES: string[] = [
  ShippingMethod.DOMESTIC_LIVE_RATE,
  ShippingMethod.INTERNATIONAL_LIVE_RATE,
  ShippingMethod.FIXED_LIVE_RATE,
  ShippingMethod.INTERNATIONAL_FIXED_LIVE_RATE,
];

const EMPTY_NOTE_SENTINEL = '__EMPTY__';
const EMPTY_NOTE_LABEL = '(none)';

interface DiscountRangeFormGroup {
  minPrice: FormControl<number>;
  maxPrice: FormControl<number | null>;
  discountType: FormControl<'percentage' | 'fixed'>;
  discountAmount: FormControl<number>;
}

interface DiscountCardFormGroup {
  shippingMethodName: FormControl<string>;
  notes: FormControl<string[]>;
  ranges: FormArray<FormGroup<DiscountRangeFormGroup>>;
}

@Component({
  selector: 'checkout-add-discount',
  templateUrl: './add-discount.component.html',
  styleUrls: ['./add-discount.component.scss'],
  standalone: false,
})
export class AddDiscountComponent implements OnInit, OnChanges {
  @ViewChildren('cardCollapsed') cardCollapsedRefs: QueryList<PdqCollapsed>;

  @Input() formGroup: UntypedFormGroup;
  @Input() settings: CheckoutSettings;
  @Input() shippings: Shippings[];

  discountCards: FormArray<FormGroup<DiscountCardFormGroup>> = new FormArray<FormGroup<DiscountCardFormGroup>>([]);
  shippingMethodOptions: DropdownOption[] = [];
  shippingMethodDefaultOptions = new Map<FormGroup<DiscountCardFormGroup>, DropdownOption>();
  notesOptionsByCard = new Map<FormGroup<DiscountCardFormGroup>, DropdownOption[]>();
  legacyDiscountWarningByCard = new Map<FormGroup<DiscountCardFormGroup>, boolean>();
  shopCurrencySign: CurrencySign | string = '$';

  readonly noteMatchingTitle = 'How note matching works';
  readonly noteMatchingBody =
    "At checkout, PDQ looks for a shipping method whose note contains any of the configured notes — partial match, not exact. If a delivery promise exists on the shipping method, it's included as part of the note when matching.";

  readonly discountTypeOptions: DropdownOption[] = [
    { value: 'percentage', label: 'Percentage' },
    { value: 'fixed', label: 'Fixed amount' },
  ];
  readonly formatNotesCount = (count: number): string => `${count} ${count === 1 ? 'note' : 'notes'} selected`;

  private notesByMethod = new Map<string, DropdownOption[]>();
  private noteCountsByMethod = new Map<string, Map<string, number>>();
  private destroyRef = inject(DestroyRef);
  private initializedFromSettings = false;

  constructor(private shopService: ShopService) {}

  ngOnInit(): void {
    this.shopCurrencySign = this.shopService.shopCurrencySymbol || '$';
    this.formGroup.addControl('discountRowsValidation', this.discountCards);

    this.discountCards.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.syncDiscountsToFormGroup();
      this.validateDuplicateMethods();
      this.validateMethodsAvailability();
      this.formGroup.markAsDirty();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['formGroup'] && !changes['formGroup'].firstChange) ||
      (changes['settings'] && !changes['settings'].firstChange)
    ) {
      this.discountCards.clear({ emitEvent: false });
      this.shippingMethodDefaultOptions.clear();
      this.notesOptionsByCard.clear();
      this.legacyDiscountWarningByCard.clear();
      this.initializedFromSettings = false;
      if (changes['formGroup']) {
        this.formGroup.addControl('discountRowsValidation', this.discountCards);
      }
    }

    if (changes['shippings'] && this.shippings) {
      this.buildShippingMethodAndNotesIndex();
      this.validateMethodsAvailability();
    }

    this.tryInitializeFromSettings();
  }

  onToggleChange(enabled: boolean): void {
    if (enabled && this.discountCards.length === 0) {
      this.addDiscountCard();
    }
    this.formGroup.markAsDirty();
  }

  addDiscountCard(): void {
    const card = this.createDiscountCardFormGroup();
    this.discountCards.push(card);
    this.rebuildNotesOptionsForCard(card);
  }

  onCardCollapseChange(collapsed: boolean, index: number): void {
    if (collapsed) return;
    this.cardCollapsedRefs?.forEach((card, i) => {
      if (i !== index && !card.collapsed) {
        card.toggle(true);
      }
    });
  }

  removeDiscountCard(index: number): void {
    const card = this.discountCards.at(index);
    this.discountCards.removeAt(index);
    this.notesOptionsByCard.delete(card);
    this.shippingMethodDefaultOptions.delete(card);
    this.legacyDiscountWarningByCard.delete(card);
  }

  addRangeToCard(card: FormGroup<DiscountCardFormGroup>): void {
    (card.get('ranges') as FormArray<FormGroup<DiscountRangeFormGroup>>).push(this.createRangeFormGroup());
  }

  removeRangeFromCard(card: FormGroup<DiscountCardFormGroup>, rangeIndex: number): void {
    const ranges = card.get('ranges') as FormArray<FormGroup<DiscountRangeFormGroup>>;
    if (ranges.length <= 1) return;
    ranges.removeAt(rangeIndex);
  }

  onNotesChange(options: DropdownOption[], card: FormGroup<DiscountCardFormGroup>): void {
    const selected = options.filter((o) => o.checked).map((o) => o.value as string);
    card.get('notes')?.setValue(selected);
    this.updateShippingMethodLabel(card);
    this.recomputeLegacyWarningForCard(card);
  }

  getDiscountSymbol(discountType: string): CurrencySign | string {
    return discountType === 'percentage' ? '%' : this.shopCurrencySign;
  }

  getDiscountPlaceholder(discountType: string): string {
    return discountType === 'percentage' ? 'Discount percentage (e.g., 10 for 10%)' : 'Discount amount';
  }

  onShippingMethodSelected(selectedOption: DropdownOption, cardIndex: number): void {
    const selectedName = selectedOption?.value as string;
    if (!selectedName) return;

    const card = this.discountCards.at(cardIndex);

    this.shippingMethodDefaultOptions.set(card, selectedOption);
    card.get('shippingMethodName')?.setValue(selectedName, { emitEvent: false });
    card.get('notes')?.setValue([]);
    this.rebuildNotesOptionsForCard(card);
    this.recomputeLegacyWarningForCard(card);
    this.updateShippingMethodLabel(card);
  }

  onDiscountTypeChange(card: FormGroup<DiscountCardFormGroup>, rangeIndex: number): void {
    const range = (card.get('ranges') as FormArray<FormGroup<DiscountRangeFormGroup>>).at(rangeIndex);
    range.get('discountAmount')?.setValue(0);
  }

  private createDiscountCardFormGroup(card?: ShippingMethodDiscountRule): FormGroup<DiscountCardFormGroup> {
    const ranges = (card ? rangesOf(card) : [undefined]).map((range) => this.createRangeFormGroup(range));
    return new FormGroup<DiscountCardFormGroup>({
      shippingMethodName: new FormControl<string>(card?.shippingMethodName ?? '', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      notes: new FormControl<string[]>(card?.notes ?? [], { nonNullable: true }),
      ranges: new FormArray<FormGroup<DiscountRangeFormGroup>>(ranges),
    });
  }

  private createRangeFormGroup(range?: ShippingMethodDiscountRange): FormGroup<DiscountRangeFormGroup> {
    return new FormGroup<DiscountRangeFormGroup>(
      {
        minPrice: new FormControl<number>(range?.minPrice ?? 0, { nonNullable: true }),
        maxPrice: new FormControl<number | null>(range?.maxPrice ?? null),
        discountType: new FormControl<'percentage' | 'fixed'>(range?.discountType ?? 'percentage', {
          nonNullable: true,
        }),
        discountAmount: new FormControl<number>(range?.discountAmount ?? 10, { nonNullable: true }),
      },
      { validators: AddDiscountComponent.minMaxThresholdValidator },
    );
  }

  private static minMaxThresholdValidator(control: AbstractControl): ValidationErrors | null {
    const minPrice = control.get('minPrice')?.value;
    const maxPrice = control.get('maxPrice')?.value;
    if (maxPrice != null && minPrice > maxPrice) {
      return { minExceedsMax: true };
    }
    return null;
  }

  private buildShippingMethodAndNotesIndex(): void {
    this.shippingMethodOptions = [];
    this.notesByMethod.clear();
    this.noteCountsByMethod.clear();
    if (!this.shippings) return;

    this.shippings.forEach((shipping) => {
      if (!shipping.name) return;
      if (shipping.shippingMethod?.category && LIVE_RATE_CATEGORIES.includes(shipping.shippingMethod.category)) return;

      const rawNote = (shipping.notes ?? '').trim();
      const noteKey = rawNote === '' ? EMPTY_NOTE_SENTINEL : rawNote;

      if (!this.noteCountsByMethod.has(shipping.name)) {
        this.noteCountsByMethod.set(shipping.name, new Map());
      }
      const counts = this.noteCountsByMethod.get(shipping.name)!;
      counts.set(noteKey, (counts.get(noteKey) ?? 0) + 1);
    });

    this.shippingMethodOptions = Array.from(this.noteCountsByMethod.keys())
      .sort()
      .map((name) => ({ value: name, label: name }));

    this.noteCountsByMethod.forEach((counts, method) => {
      const options: DropdownOption[] = Array.from(counts.entries())
        .sort(([a], [b]) => {
          if (a === EMPTY_NOTE_SENTINEL) return 1;
          if (b === EMPTY_NOTE_SENTINEL) return -1;
          return a.localeCompare(b);
        })
        .map(([note, count]) => ({
          value: note,
          label: `${note === EMPTY_NOTE_SENTINEL ? EMPTY_NOTE_LABEL : note} (${count})`,
        }));
      this.notesByMethod.set(method, options);
    });

    this.discountCards.controls.forEach((card) => {
      this.updateShippingMethodLabel(card);
      this.recomputeLegacyWarningForCard(card);
    });
  }

  private computeAffectedRateCount(methodName: string, selectedNotes: string[]): number {
    const counts = this.noteCountsByMethod.get(methodName);
    if (!counts) return 0;
    if (!selectedNotes.length) {
      let total = 0;
      counts.forEach((c) => (total += c));
      return total;
    }
    return selectedNotes.reduce((sum, note) => sum + (counts.get(note) ?? 0), 0);
  }

  private updateShippingMethodLabel(card: FormGroup<DiscountCardFormGroup>): void {
    const methodName = card.get('shippingMethodName')?.value;
    if (!methodName) {
      this.shippingMethodDefaultOptions.delete(card);
      return;
    }
    const selectedNotes = card.get('notes')?.value ?? [];
    const count = this.computeAffectedRateCount(methodName, selectedNotes);
    const existing = this.shippingMethodDefaultOptions.get(card);
    this.shippingMethodDefaultOptions.set(card, {
      ...(existing ?? { value: methodName }),
      value: methodName,
      label: `${methodName} (${count})`,
    });
  }

  private rebuildNotesOptionsForCard(card: FormGroup<DiscountCardFormGroup>): void {
    const method = card.get('shippingMethodName')?.value;
    const baseOptions = method ? (this.notesByMethod.get(method) ?? []) : [];
    const selected = card.get('notes')?.value ?? [];
    const options = baseOptions.map((opt) => ({ ...opt, checked: selected.includes(opt.value as string) }));
    this.notesOptionsByCard.set(card, options);
  }

  private tryInitializeFromSettings(): void {
    if (this.initializedFromSettings) return;
    if (!this.settings?.shippingMethodDiscounts?.length) return;
    if (!this.shippingMethodOptions.length) return;

    const cards = normalizeOnLoad(this.settings.shippingMethodDiscounts);

    const validMethodNames = new Set(this.shippingMethodOptions.map((o) => o.value as string));
    cards.forEach((card) => {
      const cardGroup = this.createDiscountCardFormGroup(card);
      this.discountCards.push(cardGroup, { emitEvent: false });
      if (card.shippingMethodName && validMethodNames.has(card.shippingMethodName)) {
        this.recomputeLegacyWarningForCard(cardGroup);
      }
      this.updateShippingMethodLabel(cardGroup);
      this.rebuildNotesOptionsForCard(cardGroup);
    });
    this.initializedFromSettings = true;
    this.validateDuplicateMethods();
    this.validateMethodsAvailability();
    this.syncDiscountsToFormGroup();
  }

  private validateMethodsAvailability(): void {
    if (this.shippings === null || this.shippings === undefined) return;
    const validNames = new Set(this.shippingMethodOptions.map((o) => o.value as string));
    this.discountCards.controls.forEach((card) => {
      const control = card.get('shippingMethodName');
      if (!control) return;
      const name = control.value ?? '';
      const isUnavailable = !!name && !validNames.has(name);
      const errors = control.errors ?? {};
      if (isUnavailable) {
        if (!errors['methodNotAvailable']) {
          control.setErrors({ ...errors, methodNotAvailable: true });
        }
      } else if (errors['methodNotAvailable']) {
        const { methodNotAvailable, ...rest } = errors;
        control.setErrors(Object.keys(rest).length ? rest : null);
      }
    });
  }

  private validateDuplicateMethods(): void {
    const cardsByMethod = new Map<string, FormGroup<DiscountCardFormGroup>[]>();
    this.discountCards.controls.forEach((card) => {
      const name = card.get('shippingMethodName')?.value ?? '';
      if (!name) return;
      const group = cardsByMethod.get(name) ?? [];
      group.push(card);
      cardsByMethod.set(name, group);
    });

    this.discountCards.controls.forEach((card) => {
      const control = card.get('shippingMethodName');
      if (!control) return;
      const name = control.value ?? '';
      const peers = name ? (cardsByMethod.get(name) ?? []).filter((c) => c !== card) : [];
      const isDuplicate = peers.some((peer) => this.cardsOverlap(card, peer));
      const errors = control.errors ?? {};
      if (isDuplicate) {
        if (!errors['duplicateMethod']) {
          control.setErrors({ ...errors, duplicateMethod: true });
        }
      } else if (errors['duplicateMethod']) {
        const { duplicateMethod, ...rest } = errors;
        control.setErrors(Object.keys(rest).length ? rest : null);
      }
    });
  }

  private cardsOverlap(a: FormGroup<DiscountCardFormGroup>, b: FormGroup<DiscountCardFormGroup>): boolean {
    const notesA = a.get('notes')?.value ?? [];
    const notesB = b.get('notes')?.value ?? [];
    if (!notesA.length || !notesB.length) return true;
    const setA = new Set(notesA);
    return notesB.some((note) => setA.has(note));
  }

  private recomputeLegacyWarningForCard(card: FormGroup<DiscountCardFormGroup>): void {
    const hasLegacyDiscount = !!this.shippings?.some(
      (shipping) => this.cardMatchesRate(card, shipping) && rateHasAnyDiscount(shipping),
    );
    this.legacyDiscountWarningByCard.set(card, hasLegacyDiscount);
  }

  private cardMatchesRate(card: FormGroup<DiscountCardFormGroup>, shipping: Shippings): boolean {
    const cardName = card.get('shippingMethodName')?.value ?? '';
    if (cardName !== shipping.name) return false;
    const selectedNotes = card.get('notes')?.value ?? [];
    if (!selectedNotes.length) return true;
    const rawNote = (shipping.notes ?? '').trim();
    const noteKey = rawNote === '' ? EMPTY_NOTE_SENTINEL : rawNote;
    return selectedNotes.includes(noteKey);
  }

  private syncDiscountsToFormGroup(): void {
    const discounts: ShippingMethodDiscountRule[] = this.discountCards.controls.map((card) => {
      const notes = card.get('notes')?.value ?? [];
      const ranges: ShippingMethodDiscountRange[] = (
        card.get('ranges') as FormArray<FormGroup<DiscountRangeFormGroup>>
      ).controls.map((range) => ({
        minPrice: range.get('minPrice')?.value ?? 0,
        maxPrice: range.get('maxPrice')?.value ?? null,
        discountType: range.get('discountType')?.value ?? 'percentage',
        discountAmount: range.get('discountAmount')?.value ?? 0,
      }));
      return {
        shippingMethodName: card.get('shippingMethodName')?.value ?? '',
        ...(notes.length ? { notes } : {}),
        ranges,
      };
    });
    this.formGroup.patchValue({ shippingMethodDiscounts: discounts });
  }
}

function rangesOf(d: ShippingMethodDiscountRule): ShippingMethodDiscountRange[] {
  if (d.ranges?.length) return d.ranges;
  if (d.minPrice != null && d.discountType && d.discountAmount != null) {
    return [
      {
        minPrice: d.minPrice,
        maxPrice: d.maxPrice ?? null,
        discountType: d.discountType,
        discountAmount: d.discountAmount,
      },
    ];
  }
  return [];
}

function normalizeOnLoad(input: ShippingMethodDiscountRule[] | undefined): ShippingMethodDiscountRule[] {
  const result: ShippingMethodDiscountRule[] = [];
  const legacyMergeIndex = new Map<string, number>();
  for (const item of input ?? []) {
    if (item.ranges?.length) {
      result.push({
        shippingMethodName: item.shippingMethodName,
        ...(item.notes?.length ? { notes: item.notes } : {}),
        ranges: [...item.ranges],
      });
      continue;
    }
    // Legacy flat-shape rule: merge into a single card sharing the same (method, notes) signature.
    const sortedNotes = (item.notes ?? []).slice().sort();
    const sig = `${item.shippingMethodName}|${sortedNotes.join(',')}`;
    const ranges = rangesOf(item);
    const existingIdx = legacyMergeIndex.get(sig);
    if (existingIdx !== undefined) {
      result[existingIdx].ranges!.push(...ranges);
    } else {
      legacyMergeIndex.set(sig, result.length);
      result.push({
        shippingMethodName: item.shippingMethodName,
        ...(item.notes?.length ? { notes: item.notes } : {}),
        ranges,
      });
    }
  }
  return result;
}
